/**
 * PATH: packages/ui/src/components/mobile-dock/useMobileDockScroll.ts
 * FILE_ID: MASTER-MOBILEDOCK-SCROLL-HOOK-001
 * PURPOSE: Own dock overflow state and dedicated horizontal page scrolling.
 * WHY: A visual-only chevron passed taps to the next navigation tile.
 * FLOW: scroll metrics -> control visibility -> scrollBy page action.
 * DEPENDENCIES: React DOM scroll APIs.
 * RELATED: index.tsx, useMobileDockScroll.test.ts.
 */

import { useEffect, useRef, useState } from 'react'

export function useMobileDockScroll(
  itemCount: number,
  shouldShow: boolean,
  activePath: string,
) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return
    const update = () => {
      setCanScrollLeft(element.scrollLeft > 2)
      setCanScrollRight(
        element.scrollLeft + element.clientWidth + 2 < element.scrollWidth,
      )
    }
    update()
    element.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      element.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [itemCount, shouldShow])

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return
    const active = element.querySelector('[data-active="true"]')
    if (active instanceof HTMLElement && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    }
  }, [activePath])

  const scrollDock = (direction: -1 | 1) => {
    const element = scrollRef.current
    if (!element) return
    element.scrollBy({
      left: direction * Math.max(element.clientWidth - 96, 180),
      behavior: 'smooth',
    })
  }

  return { scrollRef, canScrollLeft, canScrollRight, scrollDock }
}
